import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Badge, Button, Modal } from '../../components/primitives';
import { ListPage, ListTable, type ListColumn } from '../../components/list';
import {
  DocBlock,
  DocRow,
  DocInlineDate,
  DocPage,
  DocInlineAresName,
  DocInlineAresLookup,
  useDocRows,
  DocCustomRows,
  DocAddRowButton,
} from '../../components/document';
import {
  ContactPickerButton,
  type ContactPerson,
  type AresPickResult,
} from '../../components/ares';
import {
  StavbaPickerButton,
  SaveStavbaButton,
  type StavbaPickResult,
} from '../../components/stavba';
import { MapPickerButton } from '../../components/map';
import { buildDocHtml } from '../../lib/docHtml';
import { printToPdf } from '../../lib/printToPdf';
import { useActiveStavba } from '../stavba-context/StavbaContext';
import { useDocuments, useSaveDocument } from '../../api/hooks';
import type { SavedDocument } from '../../api/schemas';

/**
 * Kniha úrazů — Evidence o pracovním úrazu.
 * Struktura formuláře převzata z originálu (frontend/index.html, tab-urazy) dle
 * § 2 NV č. 201/2010 Sb. a § 105 zákona č. 262/2006 Sb. (zákoník práce).
 */

// === Číselníky z originální šablony ===
const DRUH_URAZU = [
  'smrtelný',
  's hospitalizací >5 dní',
  's prac. neschopností',
  'bez prac. neschopnosti',
] as const;

const ZDROJ_URAZU = [
  'dopravní prostředek',
  'stroje a zař. přenosná',
  'materiál, břemena',
  'pád z výšky / do hloubky',
  'nástroj, nářadí',
  'průmyslové škodliviny',
  'horké látky, oheň, výbušniny',
  'stroje a zař. stabilní',
  'elektrická energie',
  'lidé, zvířata, přírodní živly',
  'jiný zdroj',
] as const;

const PRICINY_URAZU = [
  'porucha / vadný stav zdroje',
  'špatné vyhodnocení rizika',
  'závady na pracovišti',
  'nedostatečné osobní zajištění',
  'porušení předpisů zaměstnancem',
  'nepředvídatelné riziko / selhání lidského činitele',
  'jiný důvod',
] as const;

const ZAVAZNOST_FROM_DRUH: Record<string, 'lehky' | 'registrovany' | 'tezky'> = {
  'smrtelný': 'tezky',
  's hospitalizací >5 dní': 'tezky',
  's prac. neschopností': 'registrovany',
  'bez prac. neschopnosti': 'lehky',
};

interface UrazData {
  // identifikace zaměstnavatele / stavby
  zamestnavatel: string;
  ico: string;
  stavbaInfo: string;
  adresa: string;
  // postižený
  kdo: string;
  datumNarozeni: string;
  pohlavi: string;
  obcanstvi: string;
  pracovniZarazeni: string;
  // úraz
  datum: string;
  hodina: string;
  odpracovanoHodin: string;
  misto: string;
  cinnost: string;
  kodZraneni: string;
  kodCastiTela: string;
  pocetZranenych: string;
  // checkboxy
  druh: string[];
  zdroj: string[];
  priciny: string[];
  // popis + svědci
  popis: string;
  svedci: string;
  zapisProvedl: string;
  // neschopenka (délka)
  neschopenka: string;
  // podpisy
  signStavbyvedouci: string;
  signStavbyvedouciFirma: string;
  signZamestnanec: string;
}

interface UrazRow extends SavedDocument {
  kdo?: string;
  misto?: string;
  zavaznost?: 'lehky' | 'tezky' | 'registrovany';
  neschopenka?: string;
}

const zavaznostMap: Record<string, { tone: 'neutral' | 'yellow' | 'danger'; label: string }> = {
  lehky: { tone: 'neutral', label: 'Drobný' },
  registrovany: { tone: 'yellow', label: 'Registrovaný' },
  tezky: { tone: 'danger', label: 'Těžký' },
};

function todayCz(): string {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}. ${String(d.getMonth() + 1).padStart(2, '0')}. ${d.getFullYear()}`;
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('cs-CZ');
}

function emptyForm(): UrazData {
  return {
    zamestnavatel: '',
    ico: '',
    stavbaInfo: '',
    adresa: '',
    kdo: '',
    datumNarozeni: '',
    pohlavi: '',
    obcanstvi: 'česká',
    pracovniZarazeni: '',
    datum: todayCz(),
    hodina: '',
    odpracovanoHodin: '',
    misto: '',
    cinnost: '',
    kodZraneni: '',
    kodCastiTela: '',
    pocetZranenych: '1',
    druh: [],
    zdroj: [],
    priciny: [],
    popis: '',
    svedci: '',
    zapisProvedl: '',
    neschopenka: '',
    signStavbyvedouci: '',
    signStavbyvedouciFirma: '',
    signZamestnanec: '',
  };
}

export function UrazyPage() {
  const { activeStavba } = useActiveStavba();
  const { data: docs = [], isLoading, error } = useDocuments({
    doc_type: 'uraz',
    stavba: activeStavba?.nazev,
  });
  const save = useSaveDocument();

  const [openForm, setOpenForm] = useState(false);
  const [openBlank, setOpenBlank] = useState(false);
  const blankRef = useRef<HTMLDivElement | null>(null);
  const [form, setForm] = useState<UrazData>(emptyForm());
  // Skryté standardní řádky + vlastní řádky v hlavičkových sekcích záznamu (01–03, 08).
  // Jméno `docRows` — `rows` už v komponentě patří řádkům seznamu úrazů.
  const docRows = useDocRows();
  const patch = (p: Partial<UrazData>) => setForm((f) => ({ ...f, ...p }));
  const toggleArr = (key: 'druh' | 'zdroj' | 'priciny', value: string) =>
    setForm((f) => {
      const has = f[key].includes(value);
      return { ...f, [key]: has ? f[key].filter((x) => x !== value) : [...f[key], value] };
    });

  // Při otevření modalu předvyplnit stavba-info z active stavby.
  useEffect(() => {
    if (openForm && activeStavba) {
      setForm((f) => ({
        ...f,
        stavbaInfo: f.stavbaInfo || activeStavba.nazev,
        adresa: f.adresa || activeStavba.adresa,
      }));
    }
  }, [openForm, activeStavba]);

  async function handleCreate() {
    if (!form.kdo.trim()) return;
    // zavaznost odvodíme z druh checkboxů (první nalezený) nebo default 'lehky'
    const zavaznost =
      form.druh.map((d) => ZAVAZNOST_FROM_DRUH[d]).find(Boolean) ?? 'lehky';
    await save.mutateAsync({
      doc_type: 'uraz',
      title: `Úraz — ${form.kdo.trim()} (${form.datum})`,
      stavba: activeStavba?.nazev ?? form.stavbaInfo,
      data_json: {
        ...form,
        zavaznost,
        radky: docRows.snapshot(),
      },
    });
    setOpenForm(false);
    setForm(emptyForm());
    docRows.reset();
  }

  const rows: UrazRow[] = docs.map((d) => {
    const data = d.data_json ?? {};
    return {
      ...d,
      kdo: (data.kdo as string) || (data.name as string) || '',
      misto: (data.misto as string) || '',
      zavaznost: (data.zavaznost as UrazRow['zavaznost']) || 'lehky',
      neschopenka: (data.neschopenka as string) || '',
    };
  });

  const columns: ListColumn<UrazRow>[] = [
    { key: 'id', label: 'Č.', width: '80px', mono: true, dim: true, render: (u) => `U-${u.id}` },
    { key: 'created_at', label: 'Datum', width: '130px', mono: true, render: (u) => formatDate(u.created_at) },
    { key: 'name', label: 'Zraněný', primary: true, width: '220px', render: (u) => u.kdo || u.title },
    { key: 'misto', label: 'Místo', dim: true, render: (u) => u.misto || '—' },
    {
      key: 'zavaznost',
      label: 'Závažnost',
      width: '140px',
      render: (u) => {
        const m = zavaznostMap[u.zavaznost ?? 'lehky'];
        return <Badge tone={m.tone} pill>{m.label}</Badge>;
      },
    },
    { key: 'neschopenka', label: 'Neschopenka', width: '130px', mono: true, dim: true, align: 'right', render: (u) => u.neschopenka || '—' },
  ];

  return (
    <>
      <ListPage
        breadcrumb={['BOZP', 'Kniha úrazů']}
        title="Kniha úrazů"
        subtitle={
          isLoading
            ? 'Načítám…'
            : error
              ? 'Chyba při načítání'
              : `${rows.length} evidovaných úrazů · dle § 105 zákoníku práce a § 2 NV č. 201/2010 Sb.`
        }
        status={isLoading ? 'neutral' : error ? 'draft' : 'done'}
        statusText={isLoading ? 'Načítám…' : error ? 'Chyba' : `${rows.length} úrazů`}
        actions={
          <>
            <Button variant="secondary" leading="⎙" onClick={() => setOpenBlank(true)}>
              Prázdný formulář (vzor)
            </Button>
            <Button variant="primary" onClick={() => setOpenForm(true)}>+ Zaznamenat úraz</Button>
          </>
        }
        searchPlaceholder="Hledat úraz…"
      >
        <ListTable
          columns={columns}
          rows={rows}
          rowKey={(u) => u.id}
          emptyTitle="Žádné evidované úrazy"
          emptyText="Díky za bezpečnou práci. Pokud dojde k úrazu, zaznamenejte ho sem."
        />
      </ListPage>

      <Modal
        open={openForm}
        onClose={() => setOpenForm(false)}
        title="Evidence o pracovním úrazu"
        size="xl"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpenForm(false)}>Zrušit</Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={save.isPending || !form.kdo.trim()}
            >
              {save.isPending ? 'Ukládám…' : 'Uložit záznam'}
            </Button>
          </>
        }
      >
        <div style={{ fontSize: 11, color: 'var(--sd-doc-dim)', marginBottom: 10 }}>
          dle § 2 NV č. 201/2010 Sb. a § 105 zákona č. 262/2006 Sb. (zákoník práce)
        </div>

        <div style={{ border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', overflow: 'hidden' }}>
          <DocBlock
            idx="01"
            title="Zaměstnavatel / stavba"
            hiddenCount={docRows.hiddenCount('s01')}
            onRestoreHidden={() => docRows.restoreSection('s01')}
            actions={
              <>
                <DocAddRowButton rows={docRows} section="s01" />
                <StavbaPickerButton
                  title="Načíst stavbu ze seznamu"
                  onPick={(p: StavbaPickResult) =>
                    patch({ stavbaInfo: p.nazev, adresa: p.adresa })
                  }
                />
                <SaveStavbaButton
                  title="Uložit stavbu do adresáře staveb"
                  build={() => ({
                    nazev: form.stavbaInfo.trim(),
                    adresa: form.adresa.trim(),
                    datum_zahajeni: '',
                  })}
                />
              </>
            }
          >
            {!docRows.isHidden('s01.zamestnavatel') && (
              <DocRow
                k="Zaměstnavatel"
                v={
                  <DocInlineAresName
                    value={form.zamestnavatel}
                    placeholder="Firma s.r.o."
                    onChange={(v) => patch({ zamestnavatel: v })}
                    onPick={(d: AresPickResult) => patch({ zamestnavatel: d.nazev, ico: d.ico })}
                  />
                }
                onRemove={() => docRows.hide('s01.zamestnavatel')}
              />
            )}
            {!docRows.isHidden('s01.ico') && (
              <DocRow
                k="IČO"
                mono
                value={form.ico}
                onChange={(v) => patch({ ico: v })}
                placeholder="12345678"
                actions={
                  <DocInlineAresLookup
                    ico={form.ico}
                    onResult={(d) => patch({ zamestnavatel: d.nazev, ico: d.ico })}
                  />
                }
                onRemove={() => docRows.hide('s01.ico')}
              />
            )}
            {!docRows.isHidden('s01.stavba') && (
              <DocRow k="Stavba / pracoviště" value={form.stavbaInfo} onChange={(v) => patch({ stavbaInfo: v })} onRemove={() => docRows.hide('s01.stavba')} />
            )}
            {!docRows.isHidden('s01.adresa') && (
              <DocRow
                k="Adresa stavby"
                value={form.adresa}
                onChange={(v) => patch({ adresa: v })}
                actions={
                  <MapPickerButton
                    value={form.adresa}
                    onApply={(v) => patch({ adresa: v })}
                    title="Adresa stavby"
                    label="Mapa"
                  />
                }
                onRemove={() => docRows.hide('s01.adresa')}
              />
            )}
            <DocCustomRows rows={docRows} section="s01" />
          </DocBlock>

          <DocBlock
            idx="02"
            title="Údaje o postiženém zaměstnanci"
            hiddenCount={docRows.hiddenCount('s02')}
            onRestoreHidden={() => docRows.restoreSection('s02')}
            actions={<DocAddRowButton rows={docRows} section="s02" />}
          >
            {/* Jméno je povinné (bez něj nejde uložit) — záměrně nejde skrýt. */}
            <DocRow
              k="Jméno a příjmení *"
              value={form.kdo}
              onChange={(v) => patch({ kdo: v })}
              placeholder="Jan Novák"
              actions={
                <ContactPickerButton
                  attachToCompany={form.zamestnavatel}
                  draftNew={{ jmeno: form.kdo, funkce: form.pracovniZarazeni }}
                  title="Vybrat osobu z adresáře"
                  onPick={(p: ContactPerson) =>
                    patch({
                      kdo: p.jmeno,
                      ...(p.funkce && !form.pracovniZarazeni ? { pracovniZarazeni: p.funkce } : {}),
                    })
                  }
                />
              }
            />
            {!docRows.isHidden('s02.narozeni') && (
              <DocRow
                k="Datum narození"
                mono
                value={form.datumNarozeni}
                onChange={(v) => patch({ datumNarozeni: v })}
                placeholder="dd. mm. rrrr"
                onRemove={() => docRows.hide('s02.narozeni')}
              />
            )}
            {!docRows.isHidden('s02.pohlavi') && (
              <DocRow
                k="Pohlaví"
                v={
                  <select
                    value={form.pohlavi}
                    onChange={(e) => patch({ pohlavi: e.target.value })}
                    style={selectStyle}
                    aria-label="Pohlaví"
                  >
                    <option value="">—</option>
                    <option value="muž">muž</option>
                    <option value="žena">žena</option>
                  </select>
                }
                onRemove={() => docRows.hide('s02.pohlavi')}
              />
            )}
            {!docRows.isHidden('s02.obcanstvi') && (
              <DocRow
                k="Státní občanství"
                value={form.obcanstvi}
                onChange={(v) => patch({ obcanstvi: v })}
                placeholder="česká"
                onRemove={() => docRows.hide('s02.obcanstvi')}
              />
            )}
            {!docRows.isHidden('s02.zarazeni') && (
              <DocRow
                k="Pracovní zařazení"
                value={form.pracovniZarazeni}
                onChange={(v) => patch({ pracovniZarazeni: v })}
                placeholder="zedník / tesař / pomocný dělník…"
                onRemove={() => docRows.hide('s02.zarazeni')}
              />
            )}
            <DocCustomRows rows={docRows} section="s02" />
          </DocBlock>

          <DocBlock
            idx="03"
            title="Údaje o úrazu"
            hiddenCount={docRows.hiddenCount('s03')}
            onRestoreHidden={() => docRows.restoreSection('s03')}
            actions={<DocAddRowButton rows={docRows} section="s03" />}
          >
            {!docRows.isHidden('s03.datum') && (
              <DocRow
                k="Datum úrazu"
                mono
                value={form.datum}
                onChange={(v) => patch({ datum: v })}
                placeholder="dd. mm. rrrr"
                actions={<DocInlineDate value={form.datum} onChange={(v) => patch({ datum: v })} />}
                onRemove={() => docRows.hide('s03.datum')}
              />
            )}
            {!docRows.isHidden('s03.hodina') && (
              <DocRow
                k="Hodina úrazu"
                mono
                value={form.hodina}
                onChange={(v) => patch({ hodina: v })}
                placeholder="14:30"
                onRemove={() => docRows.hide('s03.hodina')}
              />
            )}
            {!docRows.isHidden('s03.odpracovano') && (
              <DocRow
                k="Počet odpracovaných hodin"
                mono
                value={form.odpracovanoHodin}
                onChange={(v) => patch({ odpracovanoHodin: v })}
                placeholder="napr. 6"
                onRemove={() => docRows.hide('s03.odpracovano')}
              />
            )}
            {!docRows.isHidden('s03.misto') && (
              <DocRow
                k="Místo, kde k úrazu došlo"
                value={form.misto}
                onChange={(v) => patch({ misto: v })}
                placeholder="1.NP, lešení sever…"
                onRemove={() => docRows.hide('s03.misto')}
              />
            )}
            {!docRows.isHidden('s03.cinnost') && (
              <DocRow
                k="Činnost, při které k úrazu došlo"
                multiline
                value={form.cinnost}
                onChange={(v) => patch({ cinnost: v })}
                placeholder="Montáž bednění stropu…"
                onRemove={() => docRows.hide('s03.cinnost')}
              />
            )}
            {!docRows.isHidden('s03.kodZraneni') && (
              <DocRow
                k="Druh zranění (kód)"
                mono
                value={form.kodZraneni}
                onChange={(v) => patch({ kodZraneni: v })}
                placeholder="dle číselníku"
                onRemove={() => docRows.hide('s03.kodZraneni')}
              />
            )}
            {!docRows.isHidden('s03.kodCastiTela') && (
              <DocRow
                k="Zraněná část těla (kód)"
                mono
                value={form.kodCastiTela}
                onChange={(v) => patch({ kodCastiTela: v })}
                placeholder="dle číselníku"
                onRemove={() => docRows.hide('s03.kodCastiTela')}
              />
            )}
            {!docRows.isHidden('s03.pocet') && (
              <DocRow
                k="Počet zraněných celkem"
                mono
                value={form.pocetZranenych}
                onChange={(v) => patch({ pocetZranenych: v })}
                onRemove={() => docRows.hide('s03.pocet')}
              />
            )}
            <DocCustomRows rows={docRows} section="s03" />
          </DocBlock>

          <DocBlock idx="04" title="Druh úrazu">
            <CheckboxGroup
              options={DRUH_URAZU}
              selected={form.druh}
              onToggle={(v) => toggleArr('druh', v)}
              cols={2}
            />
          </DocBlock>

          <DocBlock idx="05" title="Zdroj úrazu">
            <CheckboxGroup
              options={ZDROJ_URAZU}
              selected={form.zdroj}
              onToggle={(v) => toggleArr('zdroj', v)}
              cols={2}
            />
          </DocBlock>

          <DocBlock idx="06" title="Příčiny úrazu">
            <CheckboxGroup
              options={PRICINY_URAZU}
              selected={form.priciny}
              onToggle={(v) => toggleArr('priciny', v)}
              cols={2}
            />
          </DocBlock>

          <DocBlock idx="07" title="Popis úrazového děje">
            <DocRow
              k="Popis"
              multiline
              value={form.popis}
              onChange={(v) => patch({ popis: v })}
              placeholder="Co se stalo, jak a proč…"
            />
          </DocBlock>

          <DocBlock
            idx="08"
            title="Svědci a zápis"
            hiddenCount={docRows.hiddenCount('s08')}
            onRestoreHidden={() => docRows.restoreSection('s08')}
            actions={<DocAddRowButton rows={docRows} section="s08" />}
          >
            {!docRows.isHidden('s08.svedci') && (
              <DocRow
                k="Jména svědků"
                value={form.svedci}
                onChange={(v) => patch({ svedci: v })}
                placeholder="Petr Svoboda, Karel Dvořák…"
                onRemove={() => docRows.hide('s08.svedci')}
              />
            )}
            {!docRows.isHidden('s08.zapis') && (
              <DocRow
                k="Zápis proveden (jméno, funkce)"
                value={form.zapisProvedl}
                onChange={(v) => patch({ zapisProvedl: v })}
                placeholder="Ing. Jiří Novák — stavbyvedoucí"
                actions={
                  <ContactPickerButton
                    attachToCompany={form.zamestnavatel}
                    draftNew={{ jmeno: form.zapisProvedl }}
                    title="Vybrat osobu z adresáře"
                    onPick={(p: ContactPerson) =>
                      patch({ zapisProvedl: p.funkce ? `${p.jmeno} — ${p.funkce}` : p.jmeno })
                    }
                  />
                }
                onRemove={() => docRows.hide('s08.zapis')}
              />
            )}
            {!docRows.isHidden('s08.neschopenka') && (
              <DocRow
                k="Délka neschopenky"
                mono
                value={form.neschopenka}
                onChange={(v) => patch({ neschopenka: v })}
                placeholder="např. 14 dní (nebo prázdné)"
                onRemove={() => docRows.hide('s08.neschopenka')}
              />
            )}
            <DocCustomRows rows={docRows} section="s08" />
          </DocBlock>

          <DocBlock idx="09" title="Podpisy">
            <DocRow
              k="Stavbyvedoucí — jméno"
              value={form.signStavbyvedouci}
              onChange={(v) => patch({ signStavbyvedouci: v })}
              actions={
                <ContactPickerButton
                  attachToCompany={form.signStavbyvedouciFirma || form.zamestnavatel}
                  draftNew={{ jmeno: form.signStavbyvedouci, funkce: 'stavbyvedoucí' }}
                  title="Vybrat stavbyvedoucího z adresáře"
                  onPick={(p: ContactPerson) => patch({ signStavbyvedouci: p.jmeno })}
                />
              }
            />
            <DocRow
              k="Stavbyvedoucí — firma"
              v={
                <DocInlineAresName
                  value={form.signStavbyvedouciFirma}
                  placeholder="Firma s.r.o."
                  onChange={(v) => patch({ signStavbyvedouciFirma: v })}
                  onPick={(d: AresPickResult) => patch({ signStavbyvedouciFirma: d.nazev })}
                />
              }
            />
            <DocRow
              k="Úrazem postižený zaměstnanec"
              value={form.signZamestnanec}
              onChange={(v) => patch({ signZamestnanec: v })}
              placeholder="(jméno)"
            />
          </DocBlock>
        </div>
        {save.isError && (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 4,
              background: 'var(--sd-danger-soft)',
              color: 'var(--sd-danger)',
              fontSize: 13,
            }}
          >
            Uložení selhalo: {save.error instanceof Error ? save.error.message : 'neznámá chyba'}
          </div>
        )}
      </Modal>

      <Modal
        open={openBlank}
        onClose={() => setOpenBlank(false)}
        title="Prázdný formulář — vzor k vytištění"
        size="xl"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpenBlank(false)}>Zavřít</Button>
            <Button
              variant="primary"
              leading="⎙"
              onClick={async () =>
                printToPdf('Kniha_urazu_prazdny_vzor.pdf', await buildDocHtml(blankRef.current))
              }
            >
              Tisk A4 (PDF)
            </Button>
          </>
        }
      >
        <div style={{ fontSize: 12, color: 'var(--sd-doc-dim)', marginBottom: 12 }}>
          Můžete předvyplnit hlavičku (zaměstnavatel, stavba, objednatel apod.) a vytisknout.
          Část o úrazu nechte prázdnou — vyplní se rukou na stavbě, až k něčemu dojde. Údaje
          pak přepište do evidence tlačítkem „+ Zaznamenat úraz".
        </div>
        <div ref={blankRef}>
          <BlankUrazDoc
            defaultStavba={activeStavba?.nazev ?? ''}
            defaultAdresa={activeStavba?.adresa ?? ''}
          />
        </div>
      </Modal>
    </>
  );
}

/** Editovatelná hlavička vzoru — co necháte prázdné, zůstane volné pro ruční zápis na stavbě. */
type VzorData = {
  zamestnavatel: string;
  ico: string;
  stavbaInfo: string;
  adresa: string;
  kdo: string;
  datumNarozeni: string;
  pohlavi: string;
  obcanstvi: string;
  pracovniZarazeni: string;
  datum: string;
  hodina: string;
  odpracovanoHodin: string;
  misto: string;
  cinnost: string;
  kodZraneni: string;
  kodCastiTela: string;
  pocetZranenych: string;
  popis: string;
  svedci: string;
  zapisProvedl: string;
  neschopenka: string;
  signStavbyvedouci: string;
  signStavbyvedouciFirma: string;
  signZamestnanec: string;
};

/**
 * Vzor „Evidence o pracovním úrazu" k tisku. Pole jsou editovatelná — typicky se
 * předvyplní hlavička (zaměstnavatel, stavba, objednatel), úrazová část se nechá
 * prázdná a vyplní se ručně na stavbě. Checkboxy jsou prázdné (□) k ručnímu zaškrtnutí.
 */
function BlankUrazDoc({
  defaultStavba = '',
  defaultAdresa = '',
}: {
  defaultStavba?: string;
  defaultAdresa?: string;
}) {
  const [f, setF] = useState<VzorData>(() => ({
    zamestnavatel: '',
    ico: '',
    stavbaInfo: defaultStavba,
    adresa: defaultAdresa,
    kdo: '',
    datumNarozeni: '',
    pohlavi: '',
    obcanstvi: '',
    pracovniZarazeni: '',
    datum: '',
    hodina: '',
    odpracovanoHodin: '',
    misto: '',
    cinnost: '',
    kodZraneni: '',
    kodCastiTela: '',
    pocetZranenych: '',
    popis: '',
    svedci: '',
    zapisProvedl: '',
    neschopenka: '',
    signStavbyvedouci: '',
    signStavbyvedouciFirma: '',
    signZamestnanec: '',
  }));
  const set = (key: keyof VzorData) => (v: string) => setF((s) => ({ ...s, [key]: v }));

  return (
    <DocPage
      company={{
        initials: 'KÚ',
        name: f.zamestnavatel || 'Kniha úrazů',
        address: 'Evidence o pracovním úrazu',
      }}
      tag="BOZP — Kniha úrazů"
      title="Evidence o pracovním úrazu"
      meta="dle § 2 NV č. 201/2010 Sb. a § 105 zákona č. 262/2006 Sb. (zákoník práce)"
      footer={{
        left: f.zamestnavatel || 'Kniha úrazů',
        center: f.stavbaInfo ? `Stavba: ${f.stavbaInfo}` : 'vzor',
        right: '§ 105 ZP',
      }}
    >
      <DocBlock
        idx="01"
        title="Zaměstnavatel / stavba"
        actions={
          <>
            <StavbaPickerButton
              title="Načíst stavbu ze seznamu"
              onPick={(p: StavbaPickResult) =>
                setF((s) => ({ ...s, stavbaInfo: p.nazev, adresa: p.adresa }))
              }
            />
            <SaveStavbaButton
              title="Uložit stavbu do adresáře staveb"
              build={() => ({ nazev: f.stavbaInfo.trim(), adresa: f.adresa.trim(), datum_zahajeni: '' })}
            />
          </>
        }
      >
        <DocRow
          k="Zaměstnavatel"
          v={
            <DocInlineAresName
              value={f.zamestnavatel}
              placeholder="Firma s.r.o."
              onChange={set('zamestnavatel')}
              onPick={(d: AresPickResult) =>
                setF((s) => ({ ...s, zamestnavatel: d.nazev, ico: d.ico }))
              }
            />
          }
        />
        <DocRow
          k="IČO"
          mono
          value={f.ico}
          onChange={set('ico')}
          placeholder="12345678"
          actions={
            <DocInlineAresLookup
              ico={f.ico}
              onResult={(d) => setF((s) => ({ ...s, zamestnavatel: d.nazev, ico: d.ico }))}
            />
          }
        />
        <BlankRow k="Stavba / pracoviště" value={f.stavbaInfo} onChange={set('stavbaInfo')} />
        <DocRow
          k="Adresa stavby"
          value={f.adresa}
          onChange={set('adresa')}
          actions={
            <MapPickerButton
              value={f.adresa}
              onApply={set('adresa')}
              title="Adresa stavby"
              label="Mapa"
            />
          }
        />
      </DocBlock>

      <DocBlock idx="02" title="Údaje o postiženém zaměstnanci">
        <DocRow
          k="Jméno a příjmení"
          value={f.kdo}
          onChange={set('kdo')}
          actions={
            <ContactPickerButton
              attachToCompany={f.zamestnavatel}
              draftNew={{ jmeno: f.kdo, funkce: f.pracovniZarazeni }}
              title="Vybrat osobu z adresáře"
              onPick={(p: ContactPerson) =>
                setF((s) => ({
                  ...s,
                  kdo: p.jmeno,
                  pracovniZarazeni: s.pracovniZarazeni || p.funkce,
                }))
              }
            />
          }
        />
        <BlankRow k="Datum narození" mono value={f.datumNarozeni} onChange={set('datumNarozeni')} />
        <BlankRow k="Pohlaví" value={f.pohlavi} onChange={set('pohlavi')} />
        <BlankRow k="Státní občanství" value={f.obcanstvi} onChange={set('obcanstvi')} />
        <BlankRow k="Pracovní zařazení" value={f.pracovniZarazeni} onChange={set('pracovniZarazeni')} />
      </DocBlock>

      <DocBlock idx="03" title="Údaje o úrazu">
        <BlankRow k="Datum úrazu" mono value={f.datum} onChange={set('datum')} />
        <BlankRow k="Hodina úrazu" mono value={f.hodina} onChange={set('hodina')} />
        <BlankRow k="Počet odpracovaných hodin" mono value={f.odpracovanoHodin} onChange={set('odpracovanoHodin')} />
        <BlankRow k="Místo, kde k úrazu došlo" value={f.misto} onChange={set('misto')} />
        <BlankRow k="Činnost, při které k úrazu došlo" tall value={f.cinnost} onChange={set('cinnost')} />
        <BlankRow k="Druh zranění (kód)" mono value={f.kodZraneni} onChange={set('kodZraneni')} />
        <BlankRow k="Zraněná část těla (kód)" mono value={f.kodCastiTela} onChange={set('kodCastiTela')} />
        <BlankRow k="Počet zraněných celkem" mono value={f.pocetZranenych} onChange={set('pocetZranenych')} />
      </DocBlock>

      <DocBlock idx="04" title="Druh úrazu">
        <BlankCheckGroup options={DRUH_URAZU} />
      </DocBlock>

      <DocBlock idx="05" title="Zdroj úrazu">
        <BlankCheckGroup options={ZDROJ_URAZU} />
      </DocBlock>

      <DocBlock idx="06" title="Příčiny úrazu">
        <BlankCheckGroup options={PRICINY_URAZU} />
      </DocBlock>

      <DocBlock idx="07" title="Popis úrazového děje">
        <BlankRow k="Popis" tall value={f.popis} onChange={set('popis')} />
      </DocBlock>

      <DocBlock idx="08" title="Svědci a zápis">
        <BlankRow k="Jména svědků" value={f.svedci} onChange={set('svedci')} />
        <DocRow
          k="Zápis proveden (jméno, funkce)"
          value={f.zapisProvedl}
          onChange={set('zapisProvedl')}
          actions={
            <ContactPickerButton
              attachToCompany={f.zamestnavatel}
              draftNew={{ jmeno: f.zapisProvedl }}
              title="Vybrat osobu z adresáře"
              onPick={(p: ContactPerson) =>
                setF((s) => ({
                  ...s,
                  zapisProvedl: p.funkce ? `${p.jmeno} — ${p.funkce}` : p.jmeno,
                }))
              }
            />
          }
        />
        <BlankRow k="Délka neschopenky" mono value={f.neschopenka} onChange={set('neschopenka')} />
      </DocBlock>

      <DocBlock idx="09" title="Podpisy">
        <DocRow
          k="Stavbyvedoucí — jméno"
          value={f.signStavbyvedouci}
          onChange={set('signStavbyvedouci')}
          actions={
            <ContactPickerButton
              attachToCompany={f.signStavbyvedouciFirma || f.zamestnavatel}
              draftNew={{ jmeno: f.signStavbyvedouci, funkce: 'stavbyvedoucí' }}
              title="Vybrat stavbyvedoucího z adresáře"
              onPick={(p: ContactPerson) => setF((s) => ({ ...s, signStavbyvedouci: p.jmeno }))}
            />
          }
        />
        <DocRow
          k="Stavbyvedoucí — firma"
          v={
            <DocInlineAresName
              value={f.signStavbyvedouciFirma}
              placeholder="Firma s.r.o."
              onChange={set('signStavbyvedouciFirma')}
              onPick={(d: AresPickResult) =>
                setF((s) => ({ ...s, signStavbyvedouciFirma: d.nazev }))
              }
            />
          }
        />
        <BlankRow k="Úrazem postižený zaměstnanec" value={f.signZamestnanec} onChange={set('signZamestnanec')} />
      </DocBlock>
    </DocPage>
  );
}

/**
 * Řádek vzoru — editovatelné pole. Co se nevyplní, zůstane v tisku prázdné
 * (ohraničený box k ručnímu zápisu). `tall` = víceřádkové pole.
 */
function BlankRow({
  k,
  value,
  onChange,
  tall,
  mono,
}: {
  k: ReactNode;
  value: string;
  onChange: (v: string) => void;
  tall?: boolean;
  mono?: boolean;
}) {
  return (
    <DocRow k={k} value={value} onChange={onChange} multiline={tall} mono={mono} placeholder="" />
  );
}

/** Nezaškrtnuté checkboxy (□) pro ruční zaškrtnutí na vytištěném formuláři. */
function BlankCheckGroup({ options }: { options: readonly string[] }) {
  return (
    <div
      style={{
        padding: '8px 10px',
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '6px 16px',
      }}
    >
      {options.map((o) => (
        <span
          key={o}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 12.5,
            color: 'var(--sd-doc-ink)',
          }}
        >
          <span
            style={{
              width: 13,
              height: 13,
              border: '1.5px solid var(--sd-doc-ink)',
              borderRadius: 2,
              flexShrink: 0,
            }}
          />
          <span>{o}</span>
        </span>
      ))}
    </div>
  );
}

function CheckboxGroup({
  options,
  selected,
  onToggle,
  cols = 2,
}: {
  options: readonly string[];
  selected: string[];
  onToggle: (v: string) => void;
  cols?: number;
}) {
  return (
    <div
      style={{
        padding: '8px 10px',
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: '6px 16px',
      }}
    >
      {options.map((o) => {
        const checked = selected.includes(o);
        return (
          <label
            key={o}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 12.5,
              cursor: 'pointer',
              color: 'var(--sd-doc-ink)',
            }}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => onToggle(o)}
              style={{ width: 14, height: 14, accentColor: 'var(--sd-accent)' }}
            />
            <span>{o}</span>
          </label>
        );
      })}
    </div>
  );
}

const selectStyle: React.CSSProperties = {
  padding: '3px 6px',
  border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)',
  borderRadius: 4,
  background: '#fff',
  color: 'var(--sd-doc-ink)',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};
